/**
 * ECharts configuration templates for different chart types
 */

import { EChartsOption } from 'echarts';

/**
 * Sanitize a chart title (which may include LaTeX/unicode symbols) into a
 * filesystem-safe base filename for the toolbox "save as image" download.
 */
function sanitizeFilename(title: string): string {
  const cleaned = title
    .trim()
    .replace(/[\\/:*?"<>|]/g, '')       // characters illegal in filenames
    .replace(/[^\w\s().,%±Δ-]/g, '')    // drop other symbols (keep common math/punct)
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
  return cleaned || 'chart';
}

/**
 * Shared toolbox config added to every chart option: a "save as image"
 * button in the top-right corner. Captures the chart exactly as rendered
 * (current zoom/selection state included). Filename derives from the
 * chart's own title so downloads are self-describing.
 */
function saveAsImageToolbox(title: string): EChartsOption['toolbox'] {
  return {
    show: true,
    right: 10,
    top: 4,
    feature: {
      saveAsImage: {
        pixelRatio: 2,
        name: sanitizeFilename(title),
        title: 'Save as image',
      },
    },
  };
}

/**
 * Safe min/max over arbitrarily large arrays.
 * Avoids call-stack overflow from Math.max(...largeArray) spread.
 */
function safeMinMax(arr: number[]): [number, number] {
  if (arr.length === 0) return [0, 0];
  let min = arr[0], max = arr[0];
  for (let i = 1; i < arr.length; i++) {
    if (arr[i] < min) min = arr[i];
    if (arr[i] > max) max = arr[i];
  }
  return [min, max];
}

/**
 * Create bar chart configuration
 */
export function createBarChartConfig(
  labels: string[],
  data: number[],
  title: string,
  yAxisLabel: string = '',
  isRatio: boolean = false
): EChartsOption {
  // For ratio variables, data is already (cf/baseline - 1); multiply by 100 for % display
  const displayData = isRatio
    ? data.map(v => v * 100)
    : data;

  const displayYAxisLabel = isRatio ? `${yAxisLabel} (% change)` : yAxisLabel;

  return {
    title: {
      text: title,
      left: 0,
      textStyle: {
        fontSize: 14,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    },
    toolbox: saveAsImageToolbox(title),
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: '#fff',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.1)',
      textStyle: { color: '#334155', fontSize: 12 },
      formatter: (params: any) => {
        if (!params) return '';
        const param = Array.isArray(params) ? params[0] : params;
        if (!param || param.value === undefined) return '';

        if (isRatio && typeof param.value === 'number') {
          return `${param.name}<br/>Change: ${param.value >= 0 ? '+' : ''}${param.value.toFixed(2)}%`;
        }
        const value = typeof param.value === 'number' ? param.value.toFixed(4) : param.value;
        return `${param.name}<br/>${param.seriesName}: ${value}`;
      },
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        rotate: 45,
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    yAxis: {
      type: 'value',
      name: displayYAxisLabel,
      axisLabel: {
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: false, moveOnMouseWheel: false },
    ],
    series: [
      {
        name: displayYAxisLabel || 'Value',
        type: 'bar',
        data: isRatio
          ? displayData.map(v => ({
              value: v,
              itemStyle: { color: v >= 0 ? '#22c55e' : '#ef4444' },
            }))
          : displayData,
        itemStyle: isRatio ? undefined : { color: '#2f6f79' },
        emphasis: {
          itemStyle: {
            opacity: 0.8,
          },
        },
      },
    ],
  };
}

/**
 * Create heatmap configuration
 */
export function createHeatmapConfig(
  xLabels: string[],
  yLabels: string[],
  data: number[][],
  title: string,
  xAxisName: string = '',
  yAxisName: string = '',
  isRatio: boolean = false
): EChartsOption {
  // For ratio variables, data is already (cf/baseline - 1); multiply by 100 for % display
  const heatmapData = [];
  for (let i = 0; i < yLabels.length; i++) {
    for (let j = 0; j < xLabels.length; j++) {
      const rawValue = data[i] && data[i][j] !== undefined ? data[i][j] : 0;
      const displayValue = isRatio ? rawValue * 100 : rawValue;
      heatmapData.push([j, i, displayValue]);
    }
  }

  // Calculate min and max for color scale (using display values)
  const displayValues = heatmapData.map(d => d[2]).filter(v => v !== undefined && !isNaN(v as number)) as number[];

  let minVal: number;
  let maxVal: number;

  if (isRatio) {
    // Symmetric % change scale centered at 0.
    // Clamp to at least ±0.1% so ECharts visualMap always has min < max.
    const [rawMin, rawMax] = displayValues.length > 0 ? safeMinMax(displayValues) : [-5, 5];
    const maxDist = Math.max(Math.abs(rawMax), Math.abs(rawMin), 0.1);
    minVal = -maxDist;
    maxVal = maxDist;
  } else {
    // For regular variables, use symmetric scale around 0
    const absValues = displayValues.map(v => Math.abs(v));
    const maxAbs = displayValues.length > 0 ? safeMinMax(absValues)[1] : 1;
    minVal = -maxAbs;
    maxVal = maxAbs;
  }

  return {
    title: {
      text: title,
      left: 0,
      textStyle: {
        fontSize: 14,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    },
    toolbox: saveAsImageToolbox(title),
    tooltip: {
      position: 'top',
      backgroundColor: '#fff',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.1)',
      textStyle: { color: '#334155', fontSize: 12 },
      formatter: (params: any) => {
        if (!params || !params.value || !Array.isArray(params.value)) return '';
        const [x, y, displayValue] = params.value;
        if (x === undefined || y === undefined) return '';
        const yLabel = yLabels[y] || y;
        const xLabel = xLabels[x] || x;

        // Guard against non-finite cells (NaN/undefined/Infinity) — e.g. an
        // unconverged solve or a ragged slice — so a stray bad value never
        // silently drops the tooltip; always show *something* on hover.
        if (typeof displayValue !== 'number' || !Number.isFinite(displayValue)) {
          return `${yLabel} → ${xLabel}<br/>Value: N/A`;
        }

        if (isRatio) {
          return `${yLabel} → ${xLabel}<br/>Change: ${displayValue >= 0 ? '+' : ''}${displayValue.toFixed(2)}%`;
        } else {
          return `${yLabel} → ${xLabel}<br/>Value: ${displayValue.toLocaleString(undefined, { maximumSignificantDigits: 8 })}`;
        }
      },
    },
    grid: {
      height: '70%',
      top: '10%',
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      name: xAxisName,
      nameLocation: 'end',
      nameGap: 15,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        padding: [0, 0, 0, 10],
      },
      splitArea: {
        show: true,
      },
      axisLabel: {
        rotate: 45,
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    yAxis: {
      type: 'category',
      data: yLabels,
      name: yAxisName,
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 'bold',
      },
      splitArea: {
        show: true,
      },
      axisLabel: {
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    visualMap: {
      min: minVal,
      max: maxVal,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      formatter: isRatio ? ((value: any) => `${(value as number).toFixed(1)}%`) as any : undefined,
      inRange: {
        color: isRatio
          ? ['#ef4444', '#fca5a5', '#ffffff', '#93c5fd', '#3b82f6']
          : [
              // Standard sequential color scheme for regular variables
              '#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8',
              '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026'
            ],
      },
      precision: isRatio ? 1 : 2,
    },
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: false, moveOnMouseWheel: false },
      { type: 'inside', yAxisIndex: 0, zoomOnMouseWheel: false, moveOnMouseWheel: false },
    ],
    series: [
      {
        name: title,
        type: 'heatmap',
        data: heatmapData,
        label: {
          show: false,
        },
        itemStyle: {
          borderColor: '#fff',
          borderWidth: 1,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0, 0, 0, 0.5)',
            borderWidth: 2,
          },
        },
      },
    ],
  };
}

/**
 * Create multi-scenario grouped bar chart for 1D data
 */
export function createMultiScenarioBarChartConfig(
  labels: string[],
  scenarioData: Array<{ name: string; data: number[]; color?: string }>,
  title: string,
  yAxisLabel: string = '',
  isRatio: boolean = false
): EChartsOption {
  const colors = SCENARIO_COLORS;

  // For ratio variables, data is already (cf/baseline - 1); multiply by 100 for % display
  const displayScenarioData = isRatio
    ? scenarioData.map(s => ({ ...s, data: s.data.map(v => v * 100) }))
    : scenarioData;

  const displayYAxisLabel = isRatio ? `${yAxisLabel} (% change)` : yAxisLabel;

  return {
    title: {
      text: title,
      left: 0,
      textStyle: {
        fontSize: 14,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
    },
    toolbox: saveAsImageToolbox(title),
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: '#fff',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.1)',
      textStyle: { color: '#334155', fontSize: 12 },
      formatter: (params: any) => {
        if (!params || !Array.isArray(params)) return '';
        let result = `${params[0].name}<br/>`;
        params.forEach((param: any) => {
          if (param && param.value !== undefined) {
            if (isRatio && typeof param.value === 'number') {
              result += `${param.marker} ${param.seriesName}: ${param.value >= 0 ? '+' : ''}${param.value.toFixed(2)}%<br/>`;
            } else {
              const value = typeof param.value === 'number' ? param.value.toFixed(4) : param.value;
              result += `${param.marker} ${param.seriesName}: ${value}<br/>`;
            }
          }
        });
        return result;
      },
    },
    legend: {
      data: displayScenarioData.map(s => s.name),
      top: 30,
      type: 'scroll',
    },
    grid: {
      left: '3%',
      right: '4%',
      bottom: '10%',
      top: '80px',
      containLabel: true,
    },
    xAxis: {
      type: 'category',
      data: labels,
      axisLabel: {
        rotate: 45,
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    yAxis: {
      type: 'value',
      name: displayYAxisLabel,
      nameTextStyle: { align: 'left' },
      axisLabel: {
        fontSize: 12,
        fontFamily: 'Inter, system-ui, sans-serif',
      },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: false, moveOnMouseWheel: false },
    ],
    series: displayScenarioData.map((scenario, idx) => ({
      name: scenario.name,
      type: 'bar',
      data: scenario.data,
      itemStyle: {
        color: scenario.color || colors[idx % colors.length],
      },
      emphasis: {
        itemStyle: {
          opacity: 0.8,
        },
      },
    })),
  };
}

/**
 * Scenario colors for consistent visual identification
 */
export const SCENARIO_COLORS = [
  '#245d82', '#bb7138', '#438479', '#866394',
  '#b54f5a', '#77863f', '#597d9f', '#8b6b53',
  '#ab6f97', '#649288', '#68727f', '#ba8c42'
];

/**
 * Create multi-scenario comparison heatmap with split cells
 * Each cell is divided into segments representing different scenarios
 */
export function createMultiScenarioHeatmapConfig(
  xLabels: string[],
  yLabels: string[],
  scenarioData: Array<{ name: string; data: number[][] }>,
  title: string,
  xAxisName: string = '',
  yAxisName: string = '',
  isRatio: boolean = false
): EChartsOption {
  const numScenarios = scenarioData.length;

  // Calculate global min/max across all scenarios for consistent coloring
  // For ratio variables, data is already (cf/baseline - 1); multiply by 100 for % display
  let allRawValues: number[] = [];
  scenarioData.forEach(scenario => {
    scenario.data.forEach(row => {
      row.forEach(val => {
        if (val !== undefined && !isNaN(val)) {
          allRawValues.push(val);
        }
      });
    });
  });

  // displayValues are what we actually color/scale by
  const allDisplayValues = isRatio
    ? allRawValues.map(v => v * 100)
    : allRawValues;

  let minVal: number;
  let maxVal: number;

  if (isRatio) {
    // Symmetric % change scale centered at 0.
    // Clamp to at least ±0.1% so color interpolation always has a valid range.
    const [rawMin, rawMax] = allDisplayValues.length > 0 ? safeMinMax(allDisplayValues) : [-5, 5];
    const maxDist = Math.max(Math.abs(rawMax), Math.abs(rawMin), 0.1);
    minVal = -maxDist;
    maxVal = maxDist;
  } else {
    const absAllValues = allDisplayValues.map(v => Math.abs(v));
    const maxAbs = allDisplayValues.length > 0 ? safeMinMax(absAllValues)[1] : 1;
    minVal = -maxAbs;
    maxVal = maxAbs;
  }

  // Color interpolation function (operates on display values)
  const getColor = (displayValue: number): string => {
    const colors = isRatio
      ? ['#ef4444', '#fca5a5', '#ffffff', '#93c5fd', '#3b82f6']
      : ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026'];

    const t = Math.max(0, Math.min(1, (displayValue - minVal) / (maxVal - minVal)));
    const idx = t * (colors.length - 1);
    const lower = Math.floor(idx);
    const upper = Math.min(lower + 1, colors.length - 1);
    const frac = idx - lower;

    // Simple linear interpolation between colors
    const lowerColor = colors[lower];
    const upperColor = colors[upper];

    const lR = parseInt(lowerColor.slice(1, 3), 16);
    const lG = parseInt(lowerColor.slice(3, 5), 16);
    const lB = parseInt(lowerColor.slice(5, 7), 16);
    const uR = parseInt(upperColor.slice(1, 3), 16);
    const uG = parseInt(upperColor.slice(3, 5), 16);
    const uB = parseInt(upperColor.slice(5, 7), 16);

    const r = Math.round(lR + frac * (uR - lR));
    const g = Math.round(lG + frac * (uG - lG));
    const b = Math.round(lB + frac * (uB - lB));

    return `rgb(${r},${g},${b})`;
  };

  // Create custom render data - each cell split into segments
  // Store raw values; display conversion happens in tooltip and color mapping
  const customData: any[] = [];

  for (let i = 0; i < yLabels.length; i++) {
    for (let j = 0; j < xLabels.length; j++) {
      const cellRawValues = scenarioData.map(s => s.data[i]?.[j] ?? 0);
      customData.push({
        value: [j, i, ...cellRawValues],
        scenarioNames: scenarioData.map(s => s.name),
        displayValues: cellRawValues.map(value => isRatio ? value * 100 : value),
      });
    }
  }

  return {
    title: {
      text: title,
      subtext: `Comparing ${numScenarios} scenarios`,
      left: 0,
      textStyle: { fontSize: 14, fontFamily: 'Inter, system-ui, sans-serif' },
      subtextStyle: { fontSize: 12, color: '#666' },
    },
    toolbox: saveAsImageToolbox(title),
    tooltip: {
      backgroundColor: '#fff',
      borderColor: '#e2e8f0',
      borderRadius: 8,
      extraCssText: 'box-shadow: 0 4px 12px rgba(0,0,0,0.1)',
      textStyle: { color: '#334155', fontSize: 12 },
      formatter: (params: any) => {
        if (!params?.data) return '';
        const { value, scenarioNames } = params.data;
        const [x, y, ...cellRawValues] = value;
        const yLabel = yLabels[y] || y;
        const xLabel = xLabels[x] || x;

        let result = `<strong>${yLabel} → ${xLabel}</strong><br/>`;
        cellRawValues.forEach((rawVal: number, idx: number) => {
          // Guard against non-finite cells (NaN/undefined/Infinity) so a stray
          // bad value never silently drops the tooltip row for that scenario.
          if (typeof rawVal !== 'number' || !Number.isFinite(rawVal)) {
            result += `${scenarioNames[idx]}: N/A<br/>`;
            return;
          }
          const displayVal = isRatio ? rawVal * 100 : rawVal;
          const color = getColor(displayVal);
          if (isRatio) {
            result += `<span style="display:inline-block;width:10px;height:10px;background:${color};margin-right:5px;border-radius:2px;"></span>`;
            result += `${scenarioNames[idx]}: ${displayVal >= 0 ? '+' : ''}${displayVal.toFixed(2)}%<br/>`;
          } else {
            result += `<span style="display:inline-block;width:10px;height:10px;background:${color};margin-right:5px;border-radius:2px;"></span>`;
            result += `${scenarioNames[idx]}: ${displayVal.toLocaleString(undefined, { maximumSignificantDigits: 8 })}<br/>`;
          }
        });
        return result;
      },
    },
    legend: {
      data: scenarioData.map(s => s.name),
      top: 45,
      itemWidth: 14,
      itemHeight: 14,
    },
    grid: {
      height: '65%',
      top: '12%',
      left: '8%',
      right: '5%',
    },
    xAxis: {
      type: 'category',
      data: xLabels,
      name: xAxisName,
      nameLocation: 'end',
      nameGap: 15,
      nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
      splitArea: { show: true },
      axisLabel: { rotate: 45, fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif' },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    yAxis: {
      type: 'category',
      data: yLabels,
      name: yAxisName,
      nameLocation: 'middle',
      nameGap: 60,
      nameTextStyle: { fontSize: 14, fontWeight: 'bold' },
      splitArea: { show: true },
      axisLabel: { fontSize: 12, fontFamily: 'Inter, system-ui, sans-serif' },
      splitLine: { lineStyle: { color: '#f1f5f9' } },
    },
    visualMap: {
      min: minVal,
      max: maxVal,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      formatter: isRatio ? ((value: any) => `${(value as number).toFixed(1)}%`) as any : undefined,
      inRange: {
        color: isRatio
          ? ['#ef4444', '#fca5a5', '#ffffff', '#93c5fd', '#3b82f6']
          : ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026'],
      },
      precision: isRatio ? 1 : 2,
    },
    series: [
      {
        type: 'custom',
        renderItem: (_, api: any) => {
          const cellWidth = api.size([1, 0])[0];
          const cellHeight = api.size([0, 1])[1];
          const x = api.coord([api.value(0), api.value(1)])[0] - cellWidth / 2;
          const y = api.coord([api.value(0), api.value(1)])[1] - cellHeight / 2;

          const segmentWidth = cellWidth / numScenarios;
          const padding = 1;

          const children: any[] = Array.from({ length: numScenarios }, (__, s) => {
            const rawVal = api.value(2 + s);
            // Convert to display value for color mapping
            const displayVal = isRatio ? rawVal * 100 : rawVal;
            const color = getColor(displayVal);
            return {
              type: 'rect' as const,
              shape: {
                x: x + s * segmentWidth + padding / 2,
                y: y + padding / 2,
                width: segmentWidth - padding,
                height: cellHeight - padding,
              },
              style: {
                fill: color,
                stroke: '#fff',
                lineWidth: 0.5,
              },
            };
          });
          
          if (segmentWidth >= 48 && cellHeight >= 22) {
            for (let s = 0; s < numScenarios; s++) {
              const value = api.value(2 + s) * (isRatio ? 100 : 1);
              children.push({ type: 'text', style: { x: x + (s + .5) * segmentWidth, y: y + cellHeight / 2,
                text: Number.isFinite(value) ? Number(value.toPrecision(3)).toString() : '—', align: 'center', verticalAlign: 'middle',
                fill: '#172f3c', backgroundColor: 'rgba(255,255,255,0.88)', padding: 2, fontSize: 12 } });
            }
          }
          return {
            type: 'group' as const,
            children,
          };
        },
        data: customData,
        encode: {
          x: 0,
          y: 1,
        },
      },
    ],
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, zoomOnMouseWheel: false, moveOnMouseWheel: false },
      { type: 'inside', yAxisIndex: 0, zoomOnMouseWheel: false, moveOnMouseWheel: false },
    ],
  };
}

